import React, { useState } from 'react';
import Dialog from 'components/dialogs/Dialog';
import { IconButton, InputAdornment, styled, TextField, Tooltip, Typography } from '@mui/material';
import { moneyFormat } from 'helpers/moneyFormat';
import { marginFromSalePrice, salePriceFromMargin } from 'helpers/margin';
import MoneyInput from 'components/masked-input/MoneyInput';
import PercentInput from 'components/masked-input/PercentInput';
import { useNewSurgerie } from '../hooks/useNewSurgerie';
import { Done } from '@mui/icons-material';
import { asInputComponent } from 'components/masked-input/asInputComponent';

interface EditMarginDialogProps {
  onExited(): void;
}

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  padding: 10,
});

const Fields = styled('div')(({ theme }) => ({
  marginTop: 20,
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

function round2(value: number) {
  return Math.round(value * 100) / 100;
}

// Margem e valor total são a mesma informação: mudar um recalcula o outro.
const EditMarginDialog: React.FC<EditMarginDialogProps> = ({ onExited }) => {
  const { handleChange, surgerie } = useNewSurgerie();
  const cost = surgerie.value_budget || 0;
  const [margin, setMargin] = useState(surgerie.profit_margin);
  const [total, setTotal] = useState(surgerie.value_total);

  // margem negativa (valor abaixo do custo) não aparece no campo; o erro aparece no valor total
  const shownMargin = Math.max(round2(margin), 0);

  function handleMargin(value: number) {
    setMargin(value);

    // a margem sobre a venda tem que ser menor que 100%; acima disso o total fica como estava
    if (value < 100) setTotal(salePriceFromMargin(cost, value));
  }

  function handleTotal(value: number) {
    setTotal(value);
    setMargin(marginFromSalePrice(cost, value));
  }

  const error =
    cost <= 0
      ? 'Defina os valores do orçamento antes de ajustar o valor de venda'
      : total < cost
        ? 'O valor total não pode ser menor que o custo do orçamento'
        : margin >= 100
          ? 'A margem de lucro deve ser menor que 100%'
          : '';

  function handleSubmit() {
    if (error) return;

    // a margem exata (sem arredondar) garante que o valor total fique exatamente como foi digitado
    handleChange('profit_margin', margin);
    onExited();
  }

  return (
    <Dialog
      onExited={onExited}
      title="Editar valor de venda"
      maxWidth="md"
      ComponentActions={
        <Tooltip title="Salvar">
          <span>
            <IconButton size="small" color="inherit" disabled={!!error} onClick={handleSubmit}>
              <Done />
            </IconButton>
          </span>
        </Tooltip>
      }
    >
      <Container>
        <Typography variant="body2">
          Custo do orçamento: <b>{moneyFormat(cost)}</b>
        </Typography>
        <Typography variant="body2">
          Lucro: <b>{moneyFormat(total - cost)}</b>
        </Typography>

        <Fields>
          <TextField
            fullWidth
            label="Margem de lucro"
            variant="outlined"
            size="small"
            value={shownMargin}
            onChange={e => handleMargin(Number(e.target.value))}
            error={margin >= 100}
            helperText={margin >= 100 ? 'Deve ser menor que 100%' : 'Sobre o valor de venda'}
            slotProps={{
              input: {
                inputComponent: asInputComponent(PercentInput),
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
            }}
          />

          <TextField
            fullWidth
            label="Valor total"
            variant="outlined"
            size="small"
            value={total}
            onChange={e => handleTotal(Number(e.target.value))}
            error={!!error}
            helperText={error || 'Ao alterar o valor, a margem é recalculada'}
            slotProps={{
              input: {
                inputComponent: asInputComponent(MoneyInput),
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              },
            }}
          />
        </Fields>
      </Container>
    </Dialog>
  );
};

export default EditMarginDialog;
